import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Select,
  Tag,
  Drawer,
  List,
  Typography,
  message,
  Row,
  Col,
  Card,
  Input,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Resizable } from 'react-resizable';
import { TRADE_STATUS_LABELS, TRADE_STATUS_COLORS } from '../constants/tradeStatuses';
import {
  TRADE_OPERATION_LABELS,
  TRADE_OPERATION_COLORS,
  ALL_TRADE_OPERATIONS,
} from '../constants/tradeOperations';
import { getTrades } from '../services/api';
import { formatDisplayDate } from '../utils/dates';
import { getCookie, setCookie } from '../utils/cookies';
import TradeForm from './TradeForm';
import 'react-resizable/css/styles.css';

const { Title, Text } = Typography;
const { Option } = Select;

const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 10,
            cursor: 'col-resize',
            zIndex: 1,
          }}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

const formatAmount = (amount, currency) => {
  if (amount === null || amount === undefined) return '—';
  try {
    const numericAmount = parseFloat(amount);
    if (Number.isNaN(numericAmount)) return '—';
    return `${currency || 'USD'} ${numericAmount.toFixed(2)}`;
  } catch {
    return '—';
  }
};

const resolveTradeAmount = (record) => {
  if (record?.amount != null && record.amount !== '') return record.amount;
  const qty = parseFloat(record?.quantity);
  const price = parseFloat(record?.price);
  if (Number.isFinite(qty) && Number.isFinite(price)) {
    return (qty * price).toFixed(2);
  }
  return null;
};

const tradeDisplayDate = (record) =>
  record?.settled_at || record?.approved_at || record?.created_at;

const formatDate = (dateString) => formatDisplayDate(dateString);

const opTagColor = (op) => TRADE_OPERATION_COLORS[op] || '#8c8c8c';
const opLabel = (op) => TRADE_OPERATION_LABELS[op] || op || '—';

const TradeList = ({
  fixedPortfolioId = null,
  fixedAccountId = null,
  embedded = false,
  title: titleProp,
}) => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [operationFilter, setOperationFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: parseInt(getCookie('tradePageSize'), 10) || 10,
    total: 0,
  });
  const [columnWidths, setColumnWidths] = useState({
    trade_id: 140,
    created_at: 100,
    account: 150,
    portfolio: 150,
    instrument: 200,
    operation: 140,
    quantity: 100,
    amount: 120,
    status: 120,
    actions: 100,
  });

  const listTitle = titleProp || 'Trades';

  const tradeFilters = useMemo(
    () => ({
      portfolio: fixedPortfolioId != null ? fixedPortfolioId : undefined,
      status: statusFilter,
      operation: operationFilter,
      search: appliedSearch,
    }),
    [fixedPortfolioId, statusFilter, operationFilter, appliedSearch]
  );

  const fetchTrades = useCallback(
    async (page, pageSize) => {
      try {
        setLoading(true);
        const data = await getTrades(page, pageSize, tradeFilters);

        if (!data.results) {
          message.error('Invalid data format received');
          return;
        }

        const sortedTrades = [...data.results].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setTrades(sortedTrades);
        setPagination((prev) => ({
          ...prev,
          total: data.count || 0,
        }));
      } catch (err) {
        console.error('Error fetching trades:', err.response?.data || err.message);
        message.error('Failed to fetch trades');
      } finally {
        setLoading(false);
      }
    },
    [tradeFilters]
  );

  useEffect(() => {
    fetchTrades(pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when page/size/filters change; not when total updates
  }, [pagination.current, pagination.pageSize, fetchTrades]);

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchTrades(pagination.current, pagination.pageSize);
    }, 30000);
    return () => clearInterval(refreshInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTrades, pagination.current, pagination.pageSize]);

  const handleCreateSuccess = async () => {
    setShowForm(false);
    setEditingTrade(null);
    setStatusFilter('all');
    setOperationFilter('all');
    setAppliedSearch('');
    setSearchInput('');
    const pageSize = pagination.pageSize;
    setPagination((prev) => ({ ...prev, current: 1 }));
    try {
      setLoading(true);
      const data = await getTrades(1, pageSize, {
        portfolio: fixedPortfolioId != null ? fixedPortfolioId : undefined,
        status: 'all',
        operation: 'all',
        search: '',
      });
      if (data.results) {
        const sortedTrades = [...data.results].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setTrades(sortedTrades);
        setPagination((prev) => ({ ...prev, current: 1, total: data.count || 0 }));
      }
    } catch (err) {
      console.error('Error refreshing trades after create:', err);
      message.error('Trade saved but list refresh failed — click Refresh');
    } finally {
      setLoading(false);
    }
    message.success('Trade created successfully!');
  };

  const handleViewDetails = (trade) => {
    setSelectedTrade(trade);
    setDrawerVisible(true);
  };

  const handleEdit = (trade) => {
    setEditingTrade(trade);
    setShowForm(true);
  };

  const handleNewTrade = () => {
    setShowForm(true);
    setEditingTrade(null);
  };

  const handleTableChange = (newPagination) => {
    if (newPagination.pageSize !== pagination.pageSize) {
      setCookie('tradePageSize', newPagination.pageSize);
      newPagination.current = 1;
    }
    setPagination(newPagination);
  };

  const hidePortfolioCol = Boolean(fixedPortfolioId);

  const columns = useMemo(() => {
    const base = [
      {
        title: 'Trade ID',
        dataIndex: 'trade_id',
        key: 'trade_id',
        width: columnWidths.trade_id,
        render: (text) => text || '—',
      },
      {
        title: 'Date',
        key: 'trade_date',
        width: columnWidths.created_at,
        sorter: (a, b) => {
          const da = new Date(tradeDisplayDate(a) || 0).getTime();
          const db = new Date(tradeDisplayDate(b) || 0).getTime();
          return da - db;
        },
        defaultSortOrder: 'descend',
        render: (_, record) => formatDate(tradeDisplayDate(record)),
      },
    ];

    if (!hidePortfolioCol) {
      base.push(
        {
          title: 'Account',
          key: 'account',
          width: columnWidths.account,
          render: (_, record) => (
            <div>
              <div>{record.portfolio?.account?.name}</div>
              <div style={{ fontSize: '12px', color: '#999' }}>
                {record.portfolio?.account?.account_number}
              </div>
            </div>
          ),
        },
        {
          title: 'Portfolio',
          key: 'portfolio',
          width: columnWidths.portfolio,
          render: (_, record) => (
            <div>
              <div>{record.portfolio?.name}</div>
              <div style={{ fontSize: '12px', color: '#999' }}>ID: {record.portfolio?.id}</div>
            </div>
          ),
        }
      );
    }

    base.push(
      {
        title: 'Instrument',
        key: 'instrument',
        width: columnWidths.instrument,
        render: (_, record) => (
          <div>
            <div>{record.instrument?.name}</div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              {record.instrument?.symbol} — {record.instrument?.exchange?.code}
            </div>
          </div>
        ),
      },
      {
        title: 'Operation',
        key: 'operation',
        width: columnWidths.operation,
        render: (_, record) => (
          <Tag
            color={opTagColor(record.operation)}
            style={{
              fontWeight: 'bold',
              padding: '4px 8px',
              borderRadius: '4px',
            }}
          >
            {opLabel(record.operation)}
          </Tag>
        ),
      },
      {
        title: 'Qty',
        key: 'quantity',
        width: columnWidths.quantity,
        render: (_, record) =>
          record.quantity != null && record.quantity !== '' ? record.quantity : '—',
      },
      {
        title: 'Amount',
        key: 'amount',
        width: columnWidths.amount,
        render: (_, record) => {
          const amount = resolveTradeAmount(record);
          return amount != null ? formatAmount(amount, record.currency) : '—';
        },
      },
      {
        title: 'Status',
        key: 'status',
        width: columnWidths.status,
        render: (_, record) => {
          if (!record.status || !TRADE_STATUS_LABELS[record.status]) {
            return <Tag color="default">Unknown</Tag>;
          }
          const color = TRADE_STATUS_COLORS[record.status] || '#8c8c8c';
          return (
            <Tag
              color={color}
              style={{
                fontWeight: 'bold',
                textTransform: 'uppercase',
                padding: '4px 8px',
                borderRadius: '4px',
              }}
            >
              {TRADE_STATUS_LABELS[record.status] || record.status}
            </Tag>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: columnWidths.actions,
        render: (_, record) => (
          <Space>
            <Button type="text" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)} />
            {record.status === 'draft' && (
              <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
            )}
          </Space>
        ),
      }
    );

    return base.map((c) => ({
      ...c,
      width: columnWidths[c.key] || c.width,
    }));
  }, [columnWidths, hidePortfolioCol]);

  const resizableColumns = columns.map((col) => ({
    ...col,
    onHeaderCell: () => ({
      width: col.width,
      onResize: (_, { size }) => {
        setColumnWidths((prev) => ({ ...prev, [col.key]: size.width }));
      },
    }),
  }));

  const handleRefresh = async () => {
    message.info('Refreshing trades...');
    await fetchTrades(pagination.current, pagination.pageSize);
    message.success('Trades refreshed');
  };

  const onApplySearch = (value) => {
    setAppliedSearch((value || '').trim());
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const onStatusChange = (v) => {
    setStatusFilter(v);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const onOperationChange = (v) => {
    setOperationFilter(v);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  if (showForm) {
    return (
      <div>
        <Button
          type="link"
          onClick={() => {
            setShowForm(false);
            setEditingTrade(null);
          }}
          style={{ marginBottom: 16 }}
        >
          {embedded ? 'Back' : 'Back to Trade List'}
        </Button>
        <TradeForm
          onSuccess={handleCreateSuccess}
          editTrade={editingTrade}
          defaultAccountId={fixedAccountId}
          defaultPortfolioId={fixedPortfolioId}
          lockAccountPortfolio={Boolean(fixedPortfolioId && fixedAccountId)}
        />
      </div>
    );
  }

  const filterRow = (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col xs={24} sm={8} md={5}>
        <Select style={{ width: '100%' }} value={statusFilter} onChange={onStatusChange}>
          <Option value="all">All statuses</Option>
          {Object.entries(TRADE_STATUS_LABELS).map(([value, label]) => (
            <Option key={value} value={value}>
              {label}
            </Option>
          ))}
        </Select>
      </Col>
      <Col xs={24} sm={8} md={5}>
        <Select style={{ width: '100%' }} value={operationFilter} onChange={onOperationChange}>
          <Option value="all">All operations</Option>
          {ALL_TRADE_OPERATIONS.map((op) => (
            <Option key={op} value={op}>
              {TRADE_OPERATION_LABELS[op] || op}
            </Option>
          ))}
        </Select>
      </Col>
      <Col xs={24} sm={8} md={8}>
        <Input.Search
          placeholder="Search trade id, symbol, name, ISIN"
          allowClear
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onSearch={onApplySearch}
          enterButton
        />
      </Col>
      <Col xs={24} sm={24} md={6}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleNewTrade} block>
          New trade
        </Button>
      </Col>
    </Row>
  );

  const headerRow = (
    <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
      <Col>
        <Title level={embedded ? 5 : 4}>{listTitle}</Title>
      </Col>
      <Col>
        <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
          Refresh
        </Button>
      </Col>
    </Row>
  );

  const tableBlock = (
    <>
      {headerRow}
      {filterRow}
      <Table
        columns={resizableColumns}
        dataSource={trades}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} trades`,
        }}
        onChange={handleTableChange}
        scroll={{ x: true, y: embedded ? 'calc(100vh - 520px)' : 'calc(100vh - 400px)' }}
        sticky={{ offsetHeader: 0 }}
        style={{ marginBottom: 16 }}
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
      />
      <Drawer
        title="Trade details"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={420}
      >
        {selectedTrade && (
          <List
            dataSource={[
              { label: 'Trade ID', value: selectedTrade.trade_id },
              { label: 'Date & time', value: new Date(selectedTrade.created_at).toLocaleString() },
              {
                label: 'Account',
                value: (
                  <div>
                    <div>{selectedTrade.portfolio?.account?.name}</div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      {selectedTrade.portfolio?.account?.account_number}
                    </div>
                  </div>
                ),
              },
              {
                label: 'Portfolio',
                value: (
                  <div>
                    <div>{selectedTrade.portfolio?.name}</div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      ID: {selectedTrade.portfolio?.id}
                    </div>
                  </div>
                ),
              },
              {
                label: 'Instrument',
                value: (
                  <div>
                    <div>{selectedTrade.instrument?.name}</div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      {selectedTrade.instrument?.symbol} — {selectedTrade.instrument?.exchange?.code}
                    </div>
                  </div>
                ),
              },
              {
                label: 'Operation',
                value: (
                  <Tag color={opTagColor(selectedTrade.operation)} style={{ fontWeight: 'bold' }}>
                    {opLabel(selectedTrade.operation)}
                  </Tag>
                ),
              },
              {
                label: 'Quantity',
                value:
                  selectedTrade.quantity != null && selectedTrade.quantity !== ''
                    ? selectedTrade.quantity
                    : '—',
              },
              {
                label: 'Amount',
                value: (() => {
                  const amount = resolveTradeAmount(selectedTrade);
                  return amount != null
                    ? formatAmount(amount, selectedTrade.currency)
                    : '—';
                })(),
              },
              {
                label: 'Price',
                value:
                  selectedTrade.price != null && selectedTrade.price !== ''
                    ? String(selectedTrade.price)
                    : '—',
              },
              ...(selectedTrade.notes
                ? [{ label: 'Notes', value: <Text style={{ whiteSpace: 'pre-wrap' }}>{selectedTrade.notes}</Text> }]
                : []),
              {
                label: 'Status',
                value: (
                  <Tag color={TRADE_STATUS_COLORS[selectedTrade.status] || '#8c8c8c'}>
                    {TRADE_STATUS_LABELS[selectedTrade.status] || selectedTrade.status}
                  </Tag>
                ),
              },
              { label: 'Created by', value: selectedTrade.created_by || '—' },
              ...(selectedTrade.approved_by
                ? [{ label: 'Approved by', value: selectedTrade.approved_by }]
                : []),
              ...(selectedTrade.approved_at
                ? [{ label: 'Approved at', value: new Date(selectedTrade.approved_at).toLocaleString() }]
                : []),
              ...(selectedTrade.settled_at
                ? [{ label: 'Settled at', value: new Date(selectedTrade.settled_at).toLocaleString() }]
                : []),
            ]}
            renderItem={(item) => (
              <List.Item>
                <Typography.Text strong>{item.label}:</Typography.Text>
                <div style={{ marginTop: 4 }}>{item.value}</div>
              </List.Item>
            )}
          />
        )}
      </Drawer>
    </>
  );

  if (embedded) {
    return <div>{tableBlock}</div>;
  }

  return (
    <div className="app-table-page">
      <Card>{tableBlock}</Card>
    </div>
  );
};

export default TradeList;
